// src/cockpit4/components/KnowledgeVersion.tsx
// ============================================
// P1-功能7：知识版本与协作（知识中心）
//
// 功能：
//   1. 知识版本管理（每次编辑保存历史版本，可回溯）
//   2. 多人协作编辑（显示最后编辑者和编辑时间）
//   3. 评论和讨论（每条知识可添加评论，localStorage 存储）
//   4. 审批发布流程（草稿→待审→已发布 三态）
//   5. 变更通知（显示最近变更的知识列表）
//
// 数据来源：
//   - /vault-api/list + /vault-api/read 获取知识和元数据
//   - localStorage 持久化版本快照、评论、审批状态、编辑元信息
//
// localStorage 键设计：
//   xeaos:knowledge:versions:{path} - 版本快照数组
//   xeaos:knowledge:comments:{path} - 评论数组
//   xeaos:knowledge:state:{path} - 审批状态（draft / pending / published）
//   xeaos:knowledge:meta:{path} - 最后编辑者 + 时间
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  History,
  RefreshCw,
  Loader2,
  X,
  FileText,
  MessageSquare,
  GitCommit,
  User,
  Clock,
  Send,
  RotateCcw,
  Bell,
  PenLine,
} from "lucide-react";

// ============================================
// 类型定义
// ============================================

/** 笔记元信息 */
interface NoteMeta {
  /** 文件名 */
  name: string;
  /** Vault 相对路径 */
  path: string;
  /** 修改时间 */
  mtime: string;
  /** 文件大小 */
  size: number;
}

/** 审批状态 */
type ApprovalState = "draft" | "pending" | "published";

/** 版本快照 */
interface VersionSnapshot {
  /** 版本号（从 1 开始递增） */
  version: number;
  /** 内容快照（截断 2000 字符避免 localStorage 溢出） */
  content: string;
  /** 编辑者 */
  editor: string;
  /** 时间戳 ISO */
  timestamp: string;
  /** 提交说明 */
  message: string;
}

/** 评论 */
interface Comment {
  /** 评论 ID */
  id: string;
  /** 作者 */
  author: string;
  /** 评论内容 */
  content: string;
  /** 时间戳 ISO */
  timestamp: string;
}

/** 编辑元信息 */
interface EditMeta {
  /** 最后编辑者 */
  lastEditor: string;
  /** 最后编辑时间 ISO */
  lastEditAt: string;
}

// ============================================
// 常量
// ============================================

/** localStorage 键前缀 */
const STORAGE_PREFIX = "xeaos:knowledge";

/** 知识中心 Cards 路径 */
const KNOWLEDGE_CARDS_DIR = "09_Knowledge（知识中心）/Cards";

/** 当前用户名（演示用，实际可接入身份系统） */
const CURRENT_USER = "current_user";

/** 版本快照最大长度 */
const SNAPSHOT_MAX_LEN = 2000;

// ============================================
// 工具函数：localStorage 读写
// ============================================

function loadVersions(path: string): VersionSnapshot[] {
  try {
    const key = `${STORAGE_PREFIX}:versions:${path}`;
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as VersionSnapshot[]) : [];
  } catch {
    return [];
  }
}

function saveVersions(path: string, versions: VersionSnapshot[]): void {
  try {
    const key = `${STORAGE_PREFIX}:versions:${path}`;
    localStorage.setItem(key, JSON.stringify(versions));
  } catch (err) {
    console.warn("[KnowledgeVersion] 保存版本失败:", err);
  }
}

function loadComments(path: string): Comment[] {
  try {
    const key = `${STORAGE_PREFIX}:comments:${path}`;
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Comment[]) : [];
  } catch {
    return [];
  }
}

function saveComments(path: string, comments: Comment[]): void {
  try {
    const key = `${STORAGE_PREFIX}:comments:${path}`;
    localStorage.setItem(key, JSON.stringify(comments));
  } catch (err) {
    console.warn("[KnowledgeVersion] 保存评论失败:", err);
  }
}

function loadApprovalState(path: string): ApprovalState {
  try {
    const key = `${STORAGE_PREFIX}:state:${path}`;
    const raw = localStorage.getItem(key);
    if (raw === "draft" || raw === "pending" || raw === "published") return raw;
    return "draft";
  } catch {
    return "draft";
  }
}

function saveApprovalState(path: string, state: ApprovalState): void {
  try {
    const key = `${STORAGE_PREFIX}:state:${path}`;
    localStorage.setItem(key, state);
  } catch (err) {
    console.warn("[KnowledgeVersion] 保存审批状态失败:", err);
  }
}

function loadEditMeta(path: string): EditMeta | null {
  try {
    const key = `${STORAGE_PREFIX}:meta:${path}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.lastEditor === "string" && typeof parsed.lastEditAt === "string") {
      return parsed as EditMeta;
    }
    return null;
  } catch {
    return null;
  }
}

function saveEditMeta(path: string, meta: EditMeta): void {
  try {
    const key = `${STORAGE_PREFIX}:meta:${path}`;
    localStorage.setItem(key, JSON.stringify(meta));
  } catch (err) {
    console.warn("[KnowledgeVersion] 保存编辑元信息失败:", err);
  }
}

/** 生成评论 ID */
function genId(): string {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ============================================
// 主组件
// ============================================

export default function KnowledgeVersion() {
  // 笔记列表
  const [notes, setNotes] = useState<NoteMeta[]>([]);
  // 加载状态
  const [loading, setLoading] = useState(true);
  // 选中的笔记 path（用于右侧详情）
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  // 当前笔记内容
  const [currentContent, setCurrentContent] = useState("");
  // 当前笔记内容加载中
  const [contentLoading, setContentLoading] = useState(false);
  // 编辑器内容（用于编辑后保存版本）
  const [editorContent, setEditorContent] = useState("");
  // 编辑提交说明
  const [commitMessage, setCommitMessage] = useState("");
  // 是否正在保存版本
  const [saving, setSaving] = useState(false);
  // 当前编辑模式（查看 / 编辑）
  const [editMode, setEditMode] = useState(false);

  // ============================================
  // 加载知识卡片列表（递归扫描 Cards 目录）
  // ============================================
  const scanCards = useCallback(async (): Promise<NoteMeta[]> => {
    const result: NoteMeta[] = [];
    const visited = new Set<string>();
    const queue: Array<{ dir: string; depth: number }> = [
      { dir: KNOWLEDGE_CARDS_DIR, depth: 0 },
    ];
    const MAX_DEPTH = 4;
    const MAX_FILES = 300;

    while (queue.length > 0 && result.length < MAX_FILES) {
      const { dir, depth } = queue.shift()!;
      if (visited.has(dir)) continue;
      visited.add(dir);
      try {
        const url = `/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`;
        const resp = await fetch(url);
        if (!resp.ok) continue;
        const json = await resp.json();
        if (!json?.success || !Array.isArray(json.files)) continue;
        for (const f of json.files) {
          if (result.length >= MAX_FILES) break;
          if (f.isDirectory) {
            if (depth < MAX_DEPTH) {
              queue.push({ dir: f.path, depth: depth + 1 });
            }
          } else if (f.name && f.name.endsWith(".md")) {
            result.push({
              name: f.name,
              path: f.path,
              mtime: f.mtime || new Date().toISOString(),
              size: f.size || 0,
            });
          }
        }
      } catch {
        // 单目录扫描失败，跳过
      }
    }
    return result;
  }, []);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const scanned = await scanCards();
      setNotes(scanned);
    } catch (err) {
      console.warn("[KnowledgeVersion] 扫描知识卡片失败:", err);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [scanCards]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  // ============================================
  // 加载选中笔记的内容
  // ============================================
  const loadNoteContent = useCallback(async (path: string) => {
    setContentLoading(true);
    setCurrentContent("");
    setEditorContent("");
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(path)}`);
      if (resp.ok) {
        const json = await resp.json();
        if (json?.success && typeof json.content === "string") {
          setCurrentContent(json.content);
          setEditorContent(json.content);
        }
      }
    } catch (err) {
      console.warn("[KnowledgeVersion] 读取笔记内容失败:", err);
    } finally {
      setContentLoading(false);
    }
  }, []);

  // 选中笔记时加载内容
  useEffect(() => {
    if (selectedPath) {
      void loadNoteContent(selectedPath);
      setEditMode(false);
      setCommitMessage("");
    }
  }, [selectedPath, loadNoteContent]);

  // ============================================
  // 选中笔记的元信息（从 localStorage 加载）
  // ============================================
  const selectedNote = useMemo(() => notes.find((n) => n.path === selectedPath) || null, [notes, selectedPath]);

  const selectedVersions = useMemo(
    () => (selectedPath ? loadVersions(selectedPath) : []),
    [selectedPath, saving]
  );
  const selectedComments = useMemo(
    () => (selectedPath ? loadComments(selectedPath) : []),
    [selectedPath, saving]
  );
  const selectedState = useMemo(
    () => (selectedPath ? loadApprovalState(selectedPath) : "draft" as ApprovalState),
    [selectedPath, saving]
  );
  const selectedMeta = useMemo(
    () => (selectedPath ? loadEditMeta(selectedPath) : null),
    [selectedPath, saving]
  );

  // ============================================
  // 最近变更列表：综合 Vault mtime 和 localStorage 编辑元信息
  // ============================================
  const recentChanges = useMemo(() => {
    return [...notes]
      .map((note) => {
        const meta = loadEditMeta(note.path);
        // 优先用 localStorage 中的 lastEditAt，否则用文件 mtime
        const editTime = meta?.lastEditAt || note.mtime;
        const editor = meta?.lastEditor || "vault";
        const versions = loadVersions(note.path);
        return {
          note,
          editTime,
          editor,
          versionCount: versions.length,
          state: loadApprovalState(note.path),
        };
      })
      .sort((a, b) => new Date(b.editTime).getTime() - new Date(a.editTime).getTime())
      .slice(0, 10);
  }, [notes, saving]);

  // ============================================
  // 保存新版本（编辑后提交）
  // ============================================
  const handleSaveVersion = useCallback(() => {
    if (!selectedPath || !editorContent.trim()) return;
    setSaving(true);
    try {
      const existing = loadVersions(selectedPath);
      const newVersion: VersionSnapshot = {
        version: existing.length + 1,
        content: editorContent.slice(0, SNAPSHOT_MAX_LEN),
        editor: CURRENT_USER,
        timestamp: new Date().toISOString(),
        message: commitMessage.trim() || `版本 ${existing.length + 1}`,
      };
      const next = [...existing, newVersion];
      saveVersions(selectedPath, next);
      saveEditMeta(selectedPath, {
        lastEditor: CURRENT_USER,
        lastEditAt: newVersion.timestamp,
      });
      setCommitMessage("");
      setEditMode(false);
      // 如果当前是 draft 状态，保存版本不变；如果是已发布，新编辑后回退到 draft
      const currentState = loadApprovalState(selectedPath);
      if (currentState === "published") {
        saveApprovalState(selectedPath, "draft");
      }
      // 同时把新内容写入 Vault（持久化到文件系统）
      void (async () => {
        try {
          await fetch("/vault-api/write", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path: selectedPath, content: editorContent }),
          });
          setCurrentContent(editorContent);
        } catch (err) {
          console.warn("[KnowledgeVersion] 写入 Vault 失败:", err);
        }
      })();
    } catch (err) {
      console.warn("[KnowledgeVersion] 保存版本失败:", err);
    } finally {
      setSaving(false);
    }
  }, [selectedPath, editorContent, commitMessage]);

  // 回滚到指定版本
  const handleRollback = useCallback(
    (version: VersionSnapshot) => {
      if (!selectedPath) return;
      if (!window.confirm(`确认回滚到 v${version.version}？当前内容将被覆盖。`)) return;
      setSaving(true);
      try {
        const existing = loadVersions(selectedPath);
        const newVersion: VersionSnapshot = {
          version: existing.length + 1,
          content: version.content,
          editor: CURRENT_USER,
          timestamp: new Date().toISOString(),
          message: `回滚到 v${version.version}`,
        };
        const next = [...existing, newVersion];
        saveVersions(selectedPath, next);
        saveEditMeta(selectedPath, {
          lastEditor: CURRENT_USER,
          lastEditAt: newVersion.timestamp,
        });
        setEditorContent(version.content);
        setCurrentContent(version.content);
        // 写回 Vault
        void (async () => {
          try {
            await fetch("/vault-api/write", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ path: selectedPath, content: version.content }),
            });
          } catch (err) {
            console.warn("[KnowledgeVersion] 回滚写入 Vault 失败:", err);
          }
        })();
      } finally {
        setSaving(false);
      }
    },
    [selectedPath]
  );

  // 切换审批状态
  const handleStateChange = useCallback(
    (newState: ApprovalState) => {
      if (!selectedPath) return;
      saveApprovalState(selectedPath, newState);
      setSaving((prev) => !prev); // 触发重新渲染
    },
    [selectedPath]
  );

  // ============================================
  // 评论操作
  // ============================================
  const [commentInput, setCommentInput] = useState("");
  const [commentAuthor, setCommentAuthor] = useState(CURRENT_USER);

  const handleAddComment = useCallback(() => {
    if (!selectedPath || !commentInput.trim()) return;
    const existing = loadComments(selectedPath);
    const newComment: Comment = {
      id: genId(),
      author: commentAuthor.trim() || CURRENT_USER,
      content: commentInput.trim(),
      timestamp: new Date().toISOString(),
    };
    const next = [...existing, newComment];
    saveComments(selectedPath, next);
    setCommentInput("");
    setSaving((prev) => !prev); // 触发重新渲染
  }, [selectedPath, commentInput, commentAuthor]);

  const handleDeleteComment = useCallback(
    (id: string) => {
      if (!selectedPath) return;
      const existing = loadComments(selectedPath);
      const next = existing.filter((c) => c.id !== id);
      saveComments(selectedPath, next);
      setSaving((prev) => !prev);
    },
    [selectedPath]
  );

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative h-full flex flex-col rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-cyan-500/15 border border-cyan-500/30">
            <History size={13} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">知识版本与协作</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Version · 历史回溯 · 评论讨论 · 审批发布 · 变更通知
            </p>
          </div>
        </div>
        <button
          onClick={loadNotes}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <Loader2 size={11} className="text-cyan-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-cyan-400" />
          )}
          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
            {loading ? "扫描中..." : "刷新"}
          </span>
        </button>
      </div>

      {/* 主内容：三栏（笔记列表 / 编辑器详情 / 版本+评论） */}
      <div className="flex-1 overflow-hidden flex">
        {/* 左侧：笔记列表 + 最近变更 */}
        <div className="w-64 flex-shrink-0 flex flex-col border-r border-white/[0.04]">
          <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.01]">
            <div className="flex items-center gap-1.5">
              <FileText size={11} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                知识卡片
              </span>
              <span className="text-[9px] font-mono text-gray-600 ml-auto">{notes.length}</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 size={14} className="animate-spin text-cyan-400" />
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8 text-[10px] font-mono text-gray-500">
                暂无知识卡片
              </div>
            ) : (
              notes.map((note, idx) => {
                const isSelected = selectedPath === note.path;
                const state = loadApprovalState(note.path);
                const versions = loadVersions(note.path);
                return (
                  <button
                    key={`${note.path}-${idx}`}
                    onClick={() => setSelectedPath(note.path)}
                    className={`w-full text-left px-3 py-1.5 hover:bg-white/[0.03] transition-colors border-b border-white/[0.02] ${
                      isSelected ? "bg-cyan-500/10" : ""
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <FileText size={9} className={isSelected ? "text-cyan-400" : "text-gray-600"} />
                      <span
                        className={`text-[11px] truncate flex-1 ${
                          isSelected ? "text-cyan-300" : "text-gray-300"
                        }`}
                      >
                        {note.name.replace(/\.md$/i, "")}
                      </span>
                      <StateBadge state={state} />
                    </div>
                    <div className="flex items-center justify-between text-[8px] font-mono text-gray-700">
                      <span className="truncate" title={note.path}>
                        {note.path.split("/").slice(-2, -1)[0] || "root"}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <GitCommit size={8} /> v{versions.length}
                      </span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {/* 最近变更 */}
          {!loading && recentChanges.length > 0 && (
            <div className="border-t border-white/[0.04] max-h-48 flex flex-col">
              <div className="px-3 py-1.5 border-b border-white/[0.04] bg-white/[0.01]">
                <div className="flex items-center gap-1.5">
                  <Bell size={10} className="text-amber-400" />
                  <span className="text-[9px] font-mono text-gray-400 uppercase tracking-wider">
                    最近变更
                  </span>
                </div>
              </div>
              <div className="overflow-y-auto">
                {recentChanges.slice(0, 5).map((c, idx) => (
                  <button
                    key={`${c.note.path}-${idx}`}
                    onClick={() => setSelectedPath(c.note.path)}
                    className="w-full text-left px-3 py-1 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className="flex items-center gap-1 mb-0.5">
                      <span className="text-[10px] text-gray-300 truncate flex-1">
                        {c.note.name.replace(/\.md$/i, "")}
                      </span>
                      <StateBadge state={c.state} />
                    </div>
                    <div className="text-[8px] font-mono text-gray-700">
                      {c.editor} · {new Date(c.editTime).toLocaleDateString("zh-CN")}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 中间：内容编辑器 */}
        <div className="flex-1 flex flex-col min-w-0">
          {selectedNote ? (
            <>
              <div className="px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <FileText size={11} className="text-cyan-400 flex-shrink-0" />
                    <h4 className="text-[12px] font-semibold text-white truncate">
                      {selectedNote.name.replace(/\.md$/i, "")}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1">
                    {editMode ? (
                      <>
                        <button
                          onClick={() => {
                            setEditMode(false);
                            setEditorContent(currentContent);
                          }}
                          className="px-2 py-0.5 rounded text-[9px] font-mono text-gray-400 hover:bg-white/[0.04] uppercase"
                        >
                          取消
                        </button>
                        <button
                          onClick={handleSaveVersion}
                          disabled={saving || !editorContent.trim()}
                          className="flex items-center gap-1 px-2 py-0.5 rounded bg-cyan-500/15 border border-cyan-500/30 text-[9px] font-mono text-cyan-300 uppercase hover:bg-cyan-500/25 disabled:opacity-30"
                        >
                          {saving ? (
                            <Loader2 size={9} className="animate-spin" />
                          ) : (
                            <GitCommit size={9} />
                          )}
                          保存版本
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setEditMode(true)}
                        className="flex items-center gap-1 px-2 py-0.5 rounded bg-white/[0.04] border border-white/[0.06] text-[9px] font-mono text-gray-300 uppercase hover:bg-white/[0.08]"
                      >
                        <PenLine size={9} />
                        编辑
                      </button>
                    )}
                  </div>
                </div>
                {/* 元信息 */}
                <div className="flex items-center gap-3 text-[9px] font-mono text-gray-500">
                  <span className="flex items-center gap-1">
                    <User size={9} />
                    {selectedMeta?.lastEditor || "vault"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={9} />
                    {selectedMeta
                      ? new Date(selectedMeta.lastEditAt).toLocaleString("zh-CN")
                      : new Date(selectedNote.mtime).toLocaleString("zh-CN")}
                  </span>
                  <span className="flex items-center gap-1">
                    <GitCommit size={9} />v{selectedVersions.length}
                  </span>
                  <span className="truncate" title={selectedNote.path}>
                    📁 {selectedNote.path}
                  </span>
                </div>
              </div>

              {/* 审批状态切换 */}
              <div className="px-4 py-2 border-b border-white/[0.04] bg-white/[0.01] flex items-center gap-2">
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">
                  审批状态：
                </span>
                {(["draft", "pending", "published"] as ApprovalState[]).map((s) => {
                  const isActive = selectedState === s;
                  const labels: Record<ApprovalState, string> = {
                    draft: "草稿",
                    pending: "待审",
                    published: "已发布",
                  };
                  const colors: Record<ApprovalState, string> = {
                    draft: "amber",
                    pending: "violet",
                    published: "emerald",
                  };
                  const c = colors[s];
                  return (
                    <button
                      key={s}
                      onClick={() => handleStateChange(s)}
                      className={`px-2 py-0.5 rounded text-[9px] font-mono uppercase transition-all border ${
                        isActive
                          ? `bg-${c}-500/15 text-${c}-300 border-${c}-500/30`
                          : "border-white/[0.04] text-gray-500 hover:bg-white/[0.04]"
                      }`}
                      style={
                        isActive
                          ? {
                              background:
                                c === "amber"
                                  ? "rgba(245,158,11,0.15)"
                                  : c === "violet"
                                  ? "rgba(139,92,246,0.15)"
                                  : "rgba(16,185,129,0.15)",
                              color:
                                c === "amber"
                                  ? "#FBBF24"
                                  : c === "violet"
                                  ? "#A78BFA"
                                  : "#34D399",
                              borderColor:
                                c === "amber"
                                  ? "rgba(245,158,11,0.3)"
                                  : c === "violet"
                                  ? "rgba(139,92,246,0.3)"
                                  : "rgba(16,185,129,0.3)",
                            }
                          : undefined
                      }
                    >
                      {labels[s]}
                    </button>
                  );
                })}
              </div>

              {/* 内容区域 */}
              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                {editMode ? (
                  <>
                    {/* 编辑模式 */}
                    <div className="flex-1 p-3 min-h-0">
                      <textarea
                        value={editorContent}
                        onChange={(e) => setEditorContent(e.target.value)}
                        placeholder="编辑知识内容..."
                        className="w-full h-full bg-transparent text-[12px] text-gray-200 outline-none font-mono resize-none leading-relaxed"
                      />
                    </div>
                    <div className="px-3 py-2 border-t border-white/[0.04] bg-white/[0.01]">
                      <input
                        type="text"
                        value={commitMessage}
                        onChange={(e) => setCommitMessage(e.target.value)}
                        placeholder="提交说明（可选，如：补充示例 / 修正错别字）"
                        className="w-full bg-white/[0.02] border border-white/[0.06] rounded px-2 py-1 text-[11px] text-gray-200 font-mono outline-none focus:border-cyan-500/40"
                      />
                    </div>
                  </>
                ) : (
                  /* 查看模式 */
                  <div className="flex-1 overflow-y-auto p-4 min-h-0">
                    {contentLoading ? (
                      <div className="flex items-center justify-center py-12 text-gray-500">
                        <Loader2 size={16} className="animate-spin text-cyan-400 mr-2" />
                        <span className="text-[12px] font-mono">加载内容中...</span>
                      </div>
                    ) : (
                      <pre className="text-[12px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                        {currentContent || "（暂无内容）"}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <History size={32} className="text-gray-700 mb-3" />
              <p className="text-[11px] font-mono text-gray-500 mb-1">未选中知识卡片</p>
              <p className="text-[9px] font-mono text-gray-600">
                从左侧选择一条知识卡片，查看版本历史和评论
              </p>
            </div>
          )}
        </div>

        {/* 右侧：版本历史 + 评论 */}
        <div className="w-72 flex-shrink-0 flex flex-col border-l border-white/[0.04]">
          {/* 版本历史 */}
          <div className="flex flex-col border-b border-white/[0.04]" style={{ maxHeight: "45%" }}>
            <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.01]">
              <div className="flex items-center gap-1.5">
                <GitCommit size={11} className="text-cyan-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  版本历史
                </span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">
                  {selectedVersions.length} 个版本
                </span>
              </div>
            </div>
            <div className="overflow-y-auto p-2 space-y-1.5 min-h-0">
              {selectedVersions.length === 0 ? (
                <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                  {selectedPath ? "暂无版本记录" : "请选择知识卡片"}
                </div>
              ) : (
                [...selectedVersions]
                  .sort((a, b) => b.version - a.version)
                  .map((v) => (
                    <VersionItem
                      key={v.version}
                      version={v}
                      onRollback={() => handleRollback(v)}
                    />
                  ))
              )}
            </div>
          </div>

          {/* 评论区 */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-white/[0.04] bg-white/[0.01]">
              <div className="flex items-center gap-1.5">
                <MessageSquare size={11} className="text-cyan-400" />
                <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                  评论讨论
                </span>
                <span className="text-[9px] font-mono text-gray-600 ml-auto">
                  {selectedComments.length} 条
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5 min-h-0">
              {selectedPath ? (
                selectedComments.length === 0 ? (
                  <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                    暂无评论，发表第一条评论吧
                  </div>
                ) : (
                  selectedComments.map((c) => (
                    <CommentItem
                      key={c.id}
                      comment={c}
                      onDelete={() => handleDeleteComment(c.id)}
                    />
                  ))
                )
              ) : (
                <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                  请选择知识卡片
                </div>
              )}
            </div>
            {/* 评论输入 */}
            {selectedPath && (
              <div className="p-2 border-t border-white/[0.04] bg-white/[0.01] space-y-1.5">
                <input
                  type="text"
                  value={commentAuthor}
                  onChange={(e) => setCommentAuthor(e.target.value)}
                  placeholder="你的名字"
                  className="w-full bg-white/[0.02] border border-white/[0.06] rounded px-2 py-1 text-[10px] text-gray-200 font-mono outline-none focus:border-cyan-500/40"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={commentInput}
                    onChange={(e) => setCommentInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                    placeholder="发表评论..."
                    className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded px-2 py-1 text-[11px] text-gray-200 font-mono outline-none focus:border-cyan-500/40"
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!commentInput.trim()}
                    className="flex items-center justify-center w-7 h-7 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors disabled:opacity-30"
                  >
                    <Send size={11} className="text-cyan-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部数据来源说明 */}
      <div className="px-4 py-1.5 border-t border-white/[0.04]">
        <span className="text-[8px] font-mono text-gray-600">
          数据来源：/vault-api/list + /vault-api/read + /vault-api/write · localStorage 持久化版本/评论/审批状态
        </span>
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：审批状态徽章
// ============================================
function StateBadge({ state }: { state: ApprovalState }) {
  const config: Record<ApprovalState, { label: string; color: string; bg: string }> = {
    draft: { label: "草稿", color: "#FBBF24", bg: "rgba(245,158,11,0.15)" },
    pending: { label: "待审", color: "#A78BFA", bg: "rgba(139,92,246,0.15)" },
    published: { label: "已发布", color: "#34D399", bg: "rgba(16,185,129,0.15)" },
  };
  const c = config[state];
  return (
    <span
      className="flex-shrink-0 px-1 py-0.5 rounded text-[8px] font-mono uppercase"
      style={{ color: c.color, background: c.bg }}
    >
      {c.label}
    </span>
  );
}

// ============================================
// 子组件：版本项
// ============================================
function VersionItem({
  version,
  onRollback,
}: {
  version: VersionSnapshot;
  onRollback: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="flex items-center justify-center w-5 h-5 rounded bg-cyan-500/15 text-cyan-400 text-[9px] font-mono font-bold">
            v{version.version}
          </span>
          <span className="text-[10px] text-gray-200 truncate">{version.message}</span>
        </div>
        <button
          onClick={() => setExpanded((p) => !p)}
          className="text-[9px] font-mono text-gray-500 hover:text-cyan-400 flex-shrink-0"
        >
          {expanded ? "收起" : "展开"}
        </button>
      </div>
      <div className="flex items-center gap-2 text-[9px] font-mono text-gray-600 mb-1">
        <span className="flex items-center gap-0.5">
          <User size={8} /> {version.editor}
        </span>
        <span className="flex items-center gap-0.5">
          <Clock size={8} /> {new Date(version.timestamp).toLocaleString("zh-CN")}
        </span>
      </div>
      {expanded && (
        <div className="mt-1.5 p-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
          <pre className="text-[10px] text-gray-400 whitespace-pre-wrap break-words font-mono max-h-32 overflow-y-auto">
            {version.content || "（空内容）"}
          </pre>
          <button
            onClick={onRollback}
            className="mt-1.5 flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 uppercase"
          >
            <RotateCcw size={9} />
            回滚到此版本
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// 子组件：评论项
// ============================================
function CommentItem({
  comment,
  onDelete,
}: {
  comment: Comment;
  onDelete: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-md border border-white/[0.04] bg-white/[0.02] p-2"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded-full bg-cyan-500/20 flex items-center justify-center">
            <User size={8} className="text-cyan-400" />
          </div>
          <span className="text-[10px] text-gray-200 font-medium">{comment.author}</span>
        </div>
        <button
          onClick={onDelete}
          className="p-0.5 rounded text-gray-600 hover:text-red-400 hover:bg-white/[0.04] transition-colors"
          title="删除评论"
        >
          <X size={9} />
        </button>
      </div>
      <p className="text-[10px] text-gray-300 leading-relaxed mb-1">{comment.content}</p>
      <span className="text-[8px] font-mono text-gray-600">
        {new Date(comment.timestamp).toLocaleString("zh-CN")}
      </span>
    </motion.div>
  );
}
